import React, { useState, useMemo } from 'react';
import {
  Users,
  DollarSign,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Download,
  Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mockCampaigns, mockTravelers, mockCashBoxTransactions } from '../../data/mockData';

const DetailedCampaignReport = ({ selectedCampaign, dateRange }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Get campaign details
  const campaign = useMemo(() => {
    return mockCampaigns.find(c => c.id === selectedCampaign) || mockCampaigns[0];
  }, [selectedCampaign]);

  // Get campaign travelers with payment info
  const campaignTravelers = useMemo(() => {
    const travelers = mockTravelers.filter(t => t.campaignId === campaign.id);
    
    return travelers.map(traveler => {
      // Get payment information for this traveler
      const travelerPayments = mockCashBoxTransactions.filter(t => 
        t.travelerId === traveler.id && t.transactionType === 'Payment In'
      );
      const totalPaid = travelerPayments.reduce((sum, transaction) => sum + transaction.amount, 0);
      const totalDue = traveler.totalAmountDue || 0;
      const remainingBalance = totalDue - totalPaid;
      
      // Determine payment status
      let paymentStatus = 'pending';
      if (totalPaid === 0) {
        paymentStatus = 'not_paid';
      } else if (remainingBalance <= 0) {
        paymentStatus = 'paid';
      } else {
        paymentStatus = 'partial';
      }
      
      return {
        ...traveler,
        payment: {
          totalAmount: totalDue,
          paidAmount: totalPaid,
          status: paymentStatus,
          paymentType: 'cash'
        }
      };
    });
  }, [campaign.id]);

  // Filter and sort travelers
  const filteredTravelers = useMemo(() => {
    let filtered = campaignTravelers.filter(traveler => {
      const matchesSearch = (traveler.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (traveler.phone || '').includes(searchTerm) ||
                           (traveler.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPaymentStatus = paymentStatusFilter === 'all' || 
                                  traveler.payment.status === paymentStatusFilter;
      
      return matchesSearch && matchesPaymentStatus;
    });

    // Sort travelers
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'payment':
          return b.payment.totalAmount - a.payment.totalAmount;
        case 'status':
          return a.payment.status.localeCompare(b.payment.status);
        case 'remaining':
          return (b.payment.totalAmount - b.payment.paidAmount) - (a.payment.totalAmount - a.payment.paidAmount);
        default:
          return 0;
      }
    });

    return filtered;
  }, [campaignTravelers, searchTerm, paymentStatusFilter, sortBy]);

  // Calculate campaign statistics
  const statistics = useMemo(() => {
    const totalTravelers = campaignTravelers.length;
    const totalRevenue = campaignTravelers.reduce((sum, t) => sum + t.payment.paidAmount, 0);
    const totalExpected = campaignTravelers.reduce((sum, t) => sum + t.payment.totalAmount, 0);
    const totalRemaining = totalExpected - totalRevenue;
    
    const paidCount = campaignTravelers.filter(t => t.payment.status === 'paid').length;
    const partialCount = campaignTravelers.filter(t => t.payment.status === 'partial').length;
    const pendingCount = campaignTravelers.filter(t => t.payment.status === 'pending').length;
    const overdueCount = campaignTravelers.filter(t => t.payment.status === 'overdue').length;

    // Get campaign expenses
    const campaignExpenses = mockCashBoxTransactions.filter(t => 
      t.campaignId === campaign.id && t.transactionType === 'Expense Out'
    );
    const totalExpenses = campaignExpenses.reduce((sum, t) => sum + t.amount, 0);
    const netProfit = totalRevenue - totalExpenses;

    // Payment type breakdown
    const cashPayments = campaignTravelers.filter(t => t.payment.paymentType === 'cash').length;
    const installmentPayments = campaignTravelers.filter(t => t.payment.paymentType === 'installment').length;

    return {
      totalTravelers,
      totalRevenue,
      totalExpected,
      totalRemaining,
      totalExpenses,
      netProfit,
      paidCount,
      partialCount,
      pendingCount,
      overdueCount,
      cashPayments,
      installmentPayments,
      collectionRate: totalExpected > 0 ? ((totalRevenue / totalExpected) * 100) : 0
    };
  }, [campaignTravelers, campaign.id]);

  const StatCard = ({ title, value, icon, color, subtitle }) => {
    const Icon = icon;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {typeof value === 'number' && (title.toLowerCase().includes('amount') || title.toLowerCase().includes('revenue') || title.toLowerCase().includes('profit') || title.toLowerCase().includes('remaining') || title.toLowerCase().includes('expected'))
                ? `${value.toLocaleString()} ${t('common:currency')}`
                : value.toLocaleString()}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {subtitle}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color} flex-shrink-0 ml-4`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'partial':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'overdue':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {campaign.name}
            </h2>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{campaign.destination}</span>
              </div>
            </div>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Download className="w-4 h-4" />
          {t('common.export')}
        </button>
      </div>

      {/* Campaign Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.campaignOverview')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={t('reports.totalTravelers')}
            value={statistics.totalTravelers}
            icon={Users}
            color="bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
          />
          <StatCard
            title={t('reports.totalRevenue')}
            value={statistics.totalRevenue}
            icon={DollarSign}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
            subtitle={`${statistics.collectionRate.toFixed(1)}% ${t('reports.collected')}`}
          />
          <StatCard
            title={t('reports.remainingAmount')}
            value={statistics.totalRemaining}
            icon={AlertCircle}
            color="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title={t('reports.netProfit')}
            value={statistics.netProfit}
            icon={TrendingUp}
            color={statistics.netProfit >= 0 
              ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
              : "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
            }
          />
        </div>
      </div>

      {/* Payment Status Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.paymentStatusBreakdown')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={t('payments.status.paid')}
            value={statistics.paidCount}
            icon={CheckCircle}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
          />
          <StatCard
            title={t('payments.status.partial')}
            value={statistics.partialCount}
            icon={Clock}
            color="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title={t('payments.status.pending')}
            value={statistics.pendingCount}
            icon={AlertCircle}
            color="bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400"
          />
          <StatCard
            title={t('payments.status.overdue')}
            value={statistics.overdueCount}
            icon={AlertCircle}
            color="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
          />
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('common.allStatuses')}</option>
              <option value="paid">{t('payments.status.paid')}</option>
              <option value="partial">{t('payments.status.partial')}</option>
              <option value="pending">{t('payments.status.pending')}</option>
              <option value="overdue">{t('payments.status.overdue')}</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="name">{t('common.sortByName')}</option>
              <option value="payment">{t('common.sortByPayment')}</option>
              <option value="status">{t('common.sortByStatus')}</option>
              <option value="remaining">{t('common.sortByRemaining')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Travelers List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.travelersList')} ({filteredTravelers.length})
        </h3>
        <div>
          <div className="app-table-wrap">
            <table className="app-table w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('travelers.traveler')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('travelers.contact')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payments.paymentInfo')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payments.status.title')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payments.remaining')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTravelers.map((traveler) => {
                  const remaining = traveler.payment.totalAmount - traveler.payment.paidAmount;
                  const progress = traveler.payment.totalAmount > 0 
                    ? (traveler.payment.paidAmount / traveler.payment.totalAmount) * 100 
                    : 0;

                  return (
                    <tr key={traveler.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {traveler.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {t(`travelers.gender.${traveler.gender}`)} • {traveler.age} {t('travelers.yearsOld')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {traveler.phone}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {traveler.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-white">
                              {t(`payments.type.${traveler.payment.paymentType}`)}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            ${traveler.payment.paidAmount.toLocaleString()} / ${traveler.payment.totalAmount.toLocaleString()}
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          {getPaymentStatusIcon(traveler.payment.status)}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getPaymentStatusColor(traveler.payment.status)
                          }`}>
                            {t(`payments.status.${traveler.payment.status}`)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          ${remaining.toLocaleString()}
                        </div>
                        {remaining > 0 && (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            {t('payments.outstanding')}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {filteredTravelers.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {t('common.noResults')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('reports.noTravelersFound')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedCampaignReport;